import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CreateCustomDataProjectCommand, CreateCustomDataUploadPollingCommand, CustomDataProjectsClient, CustomDataUploadPollingDto, ICustomDataProjectsClient } from "app/ApiClient";
import { LoadingState } from "types";
import { CustomDataProjectState, CustomDataUploadUrlQuery, FileUploadInfo, initialCustomDataProjectState, initialFileInfoState } from "./customDataProjectState";
import { IState } from "slices";
import { RootState } from "@store";
import { DATA_FILE_UPLOAD_TIME_OUT } from "const/config";

const customDataProjectsClient: ICustomDataProjectsClient = new CustomDataProjectsClient(process.env.REACT_APP_API_URL);

interface CustomErrorInfo {
  key: keyof CustomDataProjectState;
  uploadState: string;
  error: string;
}

interface FileUploadParam {
  key:keyof CustomDataProjectState;
  fileInfo:FileUploadInfo;
}

export const createCustomDataProject = createAsyncThunk(
  "customDataProjects/createCustomDataProject",
  async (data: CreateCustomDataProjectCommand, { rejectWithValue }) => {
    try {
      const response = await customDataProjectsClient.createCustomDataProject({ ...data });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getUploadCustomDataUrls = createAsyncThunk(
  "customDataProjects/uploadCustomDataUrls",
  async (data: CustomDataUploadUrlQuery, { rejectWithValue }) => {
    try {
      const response = await customDataProjectsClient.getUploadDataUrls(data?.projectId, data?.projectName, data?.fileType);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const uploadDataFile = createAsyncThunk<Response, FileUploadParam, { rejectValue:CustomErrorInfo; state: RootState }>(
  "customDataProjects/uploadDataFile",
  async (data: FileUploadParam, { getState, rejectWithValue }) => {
    
    const state = getState().customDataProject;
    const controller = (state[data.key] as FileUploadInfo).cancellationToken;
    const abortSignal = controller?.signal;

    try {
      if(!data.fileInfo.presignedUploadUrl || !data.fileInfo.files) {
        throw new Error("Presigned upload url not exist")
      }

      const timeoutPromise = new Promise((_resolve, reject)=>setTimeout(()=>reject(new Error("Request time out")), DATA_FILE_UPLOAD_TIME_OUT));

      const request = fetch(data.fileInfo.presignedUploadUrl, {
        method: "PUT",
        body: data.fileInfo.files[0],
        signal: abortSignal,
        headers: {
          // We must remove content-type header because presigned url doesn't bring this information 
          // from the request, otherwise we got 403 forbidden exception
          "Content-Type": " ",
        },
      });

      const response = await Promise.race([request, timeoutPromise]) as Response;

      if(!response.ok)
          throw new Error(`Failed to upload a file: ${response.statusText}`);

      return response;
    } catch (error) {
      if(abortSignal?.aborted) {
        return rejectWithValue({key: data.key, uploadState:"aborted", error:String(error)});
      } else {
        return rejectWithValue({key: data.key, uploadState:"error", error:String(error)});
      }
    }
  },
);

export const removeUploadedFile = createAsyncThunk(
  "customDataProjects/removeUploadedFile",
  async (presignedUrl: string, { rejectWithValue }) => {
    try {
      if(!presignedUrl) {
        throw new Error("Presigned upload url not exist")
      }
      const response = await fetch(presignedUrl, {
        method: "DELETE",
      });

      if(!response.ok)
          throw new Error(`Failed to delete uploaded file: ${response.statusText}`);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getCustomDataProjectById = createAsyncThunk("projects/getById", async (id: number, { rejectWithValue }) => {
  try {  
    const result = await customDataProjectsClient.getCustomDataProjectById(id);
    return result;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getCustomDataUploadStatusByProjectId = createAsyncThunk<CustomDataUploadPollingDto, {projectId:number, originalProjectId:string}>(
  "customDataProjects/getCustomDataUploadStatusByProjectId",
  async (currentProject : {projectId:number, originalProjectId:string}, { rejectWithValue }) => {
    try {
      const response = await customDataProjectsClient.getCustomDataUploadStatusProjectById(currentProject.projectId, currentProject.originalProjectId);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getCustomDataUploadStatusByToken = createAsyncThunk(
  "customDataProjects/getCustomDataUploadStatusByToken",
  async (token:string, { rejectWithValue }) => {
    try {
      const response = await customDataProjectsClient.getCustomDataUploadStatusByToken(token);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const CreateCustomDataUploadStatusPolling = createAsyncThunk(
  "customDataProjects/CreateCustomDataUploadStatusPolling",
  async (command:CreateCustomDataUploadPollingCommand, { rejectWithValue }) => {
    try {
      const response = await customDataProjectsClient.createCustomDataUploadPolling(command);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const customDataProjectSlice = createSlice({
  name: "customDataProject",
  initialState: initialCustomDataProjectState,
  reducers: {
    setCustomUploadDialogAutoClose: (state, {payload}:PayloadAction<boolean>) => {
      state.isCustomUploadDialogAutoClose = payload;
    },
    resetCreateCustomDataProjectState: (state) => {
      state.createCustomDataProjectState = LoadingState.idle;
    },
    setCustomDataUploadDialogOpened: (state, { payload }: PayloadAction<boolean>) => {
      state.isCustomDataUploadDialogOpened = payload;
    },
    setFileFormat: (state, { payload }: PayloadAction<string>) => {
      state.fileFormat = payload;
    },
    setDataStructure: (state, { payload }: PayloadAction<string>) => {
      state.dataStructure = payload;
    },
    setCustomDataFile: (state, { payload }: PayloadAction<FileUploadParam>) => {
      if(state[payload.key] !== undefined) {
        (state[payload.key] as FileUploadInfo) = payload.fileInfo;
      }
    },
    cancelUpload: (state, action: PayloadAction<string>) => {
      const key = action.payload as keyof CustomDataProjectState;
      if(key!==null && state[key] !== undefined) {
        (state[key] as FileUploadInfo).cancellationToken?.abort();
      }
    },
    setRetryUploadInfo: (state, action: PayloadAction<string>) => {
      const key = action.payload as keyof CustomDataProjectState;
      if(state[key] !== undefined) {
        if((state[key] as FileUploadInfo).files) {
          const files = (state[key] as FileUploadInfo)?.files;
          (state[key] as FileUploadInfo) = {
            ...(state[key] as FileUploadInfo),
            files: files,
            state: LoadingState.pending,
            error: undefined, 
            cancellationToken: new AbortController(),
          };
        }
      }
    },
    removeFile: (state, action: PayloadAction<string>) => {
      const key = action.payload as keyof CustomDataProjectState;
      if(state[key] !== undefined) {
        if((state[key] as FileUploadInfo).files) {
          const presignedUrl = (state[key] as FileUploadInfo).presignedUploadUrl;
          (state[key] as FileUploadInfo) = {
            ...initialFileInfoState,
            presignedUploadUrl:presignedUrl,
          };
          // Dispatch removeUploadedFile within the slice (outside think)
          // Use setTimeout to simulate dispatch within a reducer
          if(presignedUrl) {
            setTimeout(() => {
              removeUploadedFile(presignedUrl);
            });
          }
        }
      }
    },
    setFileValidationError:(state, action: PayloadAction<{key:keyof CustomDataProjectState, error:string}>) => {
      const { key, error } = action.payload;
      if(state[key]) {
        (state[key] as FileUploadInfo).files = undefined;
        (state[key] as FileUploadInfo).validationError = error;
      }
    },
    resetCustomDataFileUploadInfo: (state) => {
      state.dataFileInfo = initialFileInfoState;
      state.dictionaryFileInfo = initialFileInfoState;
      state.isCustomDataUploadDialogOpened = false;
    },
    resetCustomDataProject: (state) => {
      state.dataFileInfo = initialFileInfoState;
      state.dictionaryFileInfo = initialFileInfoState;
      state.isCustomDataUploadDialogOpened = false;
      state.pollingStatus = initialCustomDataProjectState.pollingStatus;
      state.pollingMessage = initialCustomDataProjectState.pollingMessage;
      state.token = initialCustomDataProjectState.token;
    },
  },
  extraReducers: (builder) => {
    // create custom data project
    builder.addCase(createCustomDataProject.pending, (state) => {
      state.createCustomDataProjectState = LoadingState.pending;
      state.customDataProject = null;
    });
    builder.addCase(createCustomDataProject.fulfilled, (state, { payload }) => {
      state.createCustomDataProjectState = LoadingState.succeed;
      state.customDataProject = payload;
    });
    builder.addCase(createCustomDataProject.rejected, (state) => {
      state.createCustomDataProjectState = LoadingState.failed;
    });
    // get custom data project
    builder.addCase(getCustomDataProjectById.pending, (state) => {
      state.getCustomDataProjectLoadingState = LoadingState.pending;
    });
    builder.addCase(getCustomDataProjectById.fulfilled, (state, action) => {
      state.getCustomDataProjectLoadingState = LoadingState.succeed;
      state.customDataProject = action.payload;
    });
    builder.addCase(getCustomDataProjectById.rejected, (state, action) => {
      state.getCustomDataProjectLoadingState = LoadingState.pending;
      state.getProjectError = action.error.message;
    });
    // get upload custom data urls
    builder.addCase(getUploadCustomDataUrls.pending, (state) => {
      state.uploadUrlsLoadingState = LoadingState.pending;
    });
    builder.addCase(getUploadCustomDataUrls.fulfilled, (state, action) => {
      state.uploadUrlsLoadingState = LoadingState.succeed;
      state.uploadUrls = action.payload;
      state.preAssignedToken = action.payload.token;
      state.dataFileInfo.presignedUploadUrl = state.uploadUrls.dataPresignedUrl;
      state.dictionaryFileInfo.presignedUploadUrl = state.uploadUrls.dictionaryPresignedUrl;
    });
    builder.addCase(getUploadCustomDataUrls.rejected, (state, action) => {
      state.uploadUrlsLoadingState = LoadingState.pending;
      state.uploadUrlsError = action.error.message;
    });
    // upload data or dictionary file using presigned url
    builder.addCase(uploadDataFile.pending, (state, action) => {
      const { key } = action.meta.arg;
      const controller = new AbortController();
      if(state[key] !== undefined) {
        (state[key] as FileUploadInfo).validationError = undefined;
        (state[key] as FileUploadInfo).state = LoadingState.pending;
        (state[key] as FileUploadInfo).cancellationToken = controller;
      }
    });
    builder.addCase(uploadDataFile.fulfilled, (state, action) => {
      const { key } = action.meta.arg;
      if(state[key] !== undefined) {
        (state[key] as FileUploadInfo).state = LoadingState.succeed;
      }
    });
    builder.addCase(uploadDataFile.rejected, (state, action) => {
      if(action.payload) {
        const { key, uploadState, error } = action.payload;
        if(uploadState === "aborted") {
          (state[key] as FileUploadInfo).state = LoadingState.idle;
          (state[key] as FileUploadInfo).files = undefined;
          (state[key] as FileUploadInfo).error = undefined;
        } else {
          if(state[key] !== undefined) {
            (state[key] as FileUploadInfo).state = LoadingState.failed;
            (state[key] as FileUploadInfo).error = error;
          }
        }
      }
    });
    // get custom data upload status
    builder.addCase(getCustomDataUploadStatusByProjectId.pending, (state) => {
      state.customDataUploadPollingLoadingState = LoadingState.pending;
    });
    builder.addCase(getCustomDataUploadStatusByProjectId.fulfilled, (state, action) => {
      state.customDataUploadPollingLoadingState = LoadingState.succeed;
      if(action?.payload) {
        state.token = action.payload?.token;
        state.pollingStatus = action.payload?.status;
        state.pollingMessage = action.payload?.message;
      }
    });
    builder.addCase(getCustomDataUploadStatusByProjectId.rejected, (state, action) => {
      state.customDataUploadPollingLoadingState = LoadingState.pending;
      state.customDataUploadPollingDataErrorMessage = action.error.message;
    });
    // get custom data upload status
    builder.addCase(getCustomDataUploadStatusByToken.fulfilled, (state, action) => {
      state.pollingStatus = action.payload?.status;
      state.pollingMessage = action.payload?.message;
    });

    // Create custom data upload status polling
   builder.addCase(CreateCustomDataUploadStatusPolling.pending, (state) => {
    state.createCustomDataUploadPollingLoadingState = LoadingState.pending;
  });
  builder.addCase(CreateCustomDataUploadStatusPolling.fulfilled, (state, action) => {
    state.createCustomDataUploadPollingLoadingState = LoadingState.succeed;
    state.token = action.payload;
    state.pollingStatus = initialCustomDataProjectState.pollingStatus;
    state.pollingMessage = initialCustomDataProjectState.pollingMessage;
    state.isCustomUploadDialogAutoClose = false;
  });
  builder.addCase(CreateCustomDataUploadStatusPolling.rejected, (state) => {
    state.createCustomDataUploadPollingLoadingState = LoadingState.failed;
  });
  },
});

export const {
  resetCustomDataProject,
  resetCreateCustomDataProjectState,
  setCustomDataUploadDialogOpened,
  setFileFormat,
  setDataStructure,
  setCustomDataFile,
  cancelUpload,
  setRetryUploadInfo,  
  removeFile,
  setFileValidationError,
  setCustomUploadDialogAutoClose,
  resetCustomDataFileUploadInfo,
} = customDataProjectSlice.actions;
export const customDataProjectSelector = (state: IState): CustomDataProjectState => state.customDataProject;
export default customDataProjectSlice.reducer;
